import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import OffersPackageCard from '../components/OffersPackageCard';
import OffersFilters from '../components/OffersFilters';
import { GET_MARKETPLACE_PACKAGES } from '../graphql/queries/package';
import type { PackageSearchFilters } from '../types/marketplaceSearch';
import {
  packageSearchFiltersFromParams,
  paramsFromPackageSearchFilters,
} from '../types/marketplaceSearch';
import { filterPackagesBySearch } from '../utils/marketplacePackageFilter';

type MarketplacePackagesQuery = {
  packages: Array<{
    id: number;
    title: string;
    description: string;
    image: string;
    groupSize: string;
    category: string;
    type: 'B2B' | 'B2C';
    departure: string;
    cities: string[];
    countries: string[];
    departures: Array<{
      id: number;
      departureDate: string;
      returnDate: string;
    }>;
    formulas: Array<{
      id: number;
      name: string;
      priceType: string;
      perPersonPrice: number | null;
      roomPrices: {
        single: number;
        double: number;
        triple: number;
        quadruple: number;
        quintuple: number;
      };
    }>;
    agency: {
      id: number;
      name: string;
      logo: string | null;
      rating: number | null;
      verified: boolean;
      theme: { primaryColor: string | null; secondaryColor: string | null } | null;
    };
    airline?: {
      id: number;
      name: string;
      code: string | null;
      logo: string;
    } | null;
  }>;
};

function filtersActive(f: PackageSearchFilters): boolean {
  return (
    f.destination.trim() !== '' ||
    f.category !== '' ||
    f.month !== '' ||
    f.duration !== '' ||
    f.packageType !== '' ||
    f.airlineId !== ''
  );
}

export default function OffersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<PackageSearchFilters>(() =>
    packageSearchFiltersFromParams(searchParams)
  );

  useEffect(() => {
    setFilters(packageSearchFiltersFromParams(searchParams));
  }, [searchParams.toString()]);

  const { data, loading, error } = useQuery<MarketplacePackagesQuery>(GET_MARKETPLACE_PACKAGES);

  const filtered = useMemo(() => {
    const all = data?.packages ?? [];
    return filterPackagesBySearch(all, filters);
  }, [data?.packages, filters]);

  const totalCount = data?.packages?.length ?? 0;

  const handleApply = () => {
    setSearchParams(paramsFromPackageSearchFilters(filters), { replace: true });
  };

  const handleResetUrl = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[#f2f4f7] pt-24 pb-16">
        <div className="mx-auto max-w-[1280px] px-4 lg:px-6">
          <div className="mb-6 lg:mb-8">
            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Tous les séjours</h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600 md:text-base">
              Parcourez les séjours de nos agences partenaires. Utilisez la colonne de gauche pour
              filtrer comme sur un comparateur de voyages.
            </p>
          </div>

          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
            <aside className="w-full shrink-0 lg:w-[280px] xl:w-[300px]">
              <OffersFilters
                value={filters}
                onChange={setFilters}
                onSubmit={handleApply}
                onReset={handleResetUrl}
              />
            </aside>

            <section className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:p-6">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
              </div>
            ) : error ? (
              <p className="text-center text-red-600 py-16">
                Impossible de charger les séjours pour le moment.
              </p>
            ) : totalCount === 0 ? (
              <p className="text-center text-gray-500 py-16">Aucun séjour pour le moment.</p>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-6">
                  {filtered.length} résultat{filtered.length !== 1 ? 's' : ''}
                  {filtersActive(filters) && totalCount > 0
                    ? ` sur ${totalCount} séjour${totalCount !== 1 ? 's' : ''}`
                    : null}
                </p>
                {filtered.length === 0 ? (
                  <p className="text-center text-gray-500 py-12">
                    Aucun séjour ne correspond à ces critères. Essayez d’élargir la recherche ou
                    réinitialisez les filtres.
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {filtered.map((pkg) => (
                      <OffersPackageCard
                        key={pkg.id}
                        package={{
                          ...pkg,
                          departures: pkg.departures ?? [],
                          airline: pkg.airline ?? undefined,
                          agency: pkg.agency
                            ? {
                                id: pkg.agency.id,
                                name: pkg.agency.name,
                                logo: pkg.agency.logo ?? '',
                                rating: pkg.agency.rating ?? undefined,
                                verified: pkg.agency.verified,
                                secondaryColor: pkg.agency.theme?.secondaryColor ?? null,
                              }
                            : undefined,
                        }}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
